import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useNavigate } from 'react-router-dom';
import { trackFormSubmission, trackAgentApplication } from '../lib/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AgentJoinForm = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    agentName: '',
    email: '',
    phone: '',
    licenseNumber: '',
    yearsExperience: '',
    serviceAreas: '',
    agencyName: '',
    website: '',
    message: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    if (!executeRecaptcha) {
      toast.error('reCAPTCHA not loaded. Please refresh and try again.');
      return;
    }

    setIsSubmitting(true);

    // Track form submission attempt
    trackFormSubmission('agent_join_form');

    try {
      // Generate reCAPTCHA token
      const token = await executeRecaptcha('agent_join_submit');

      await axios.post(
        `${BACKEND_URL}/api/agent-join`,
        {
          ...formData,
          recaptchaToken: token
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      // Track successful agent application
      trackAgentApplication();
      
      // Reset form
      setFormData({
        agentName: '',
        email: '',
        phone: '',
        licenseNumber: '',
        yearsExperience: '',
        serviceAreas: '',
        agencyName: '',
        website: '',
        message: ''
      });
      navigate('/thank-you', { state: { submitted: true, type: 'agent' } });
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Please try again or contact us directly.";
      toast.error('Submission Failed', {
        description: errorMessage,
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [executeRecaptcha, formData, navigate]);

  return (
    <section id="agent-join" className="section-container section-alt">
      <div className="section-content">
        <div className="agent-join-header">
          <h2 className="section-title">Join Our Agent Network</h2>
          <p className="section-description">
            Are you an experienced real estate agent looking to connect with motivated buyers and sellers? Join FindMeAgent and grow your business.
          </p>
        </div>

        <div className="agent-join-wrapper">
          <div className="agent-benefits">
            <h3 className="benefits-title">Why Partner With Us?</h3>
            <ul className="benefits-list">
              <li>
                <span className="benefit-icon">✓</span>
                <span>Access to pre-qualified, motivated leads in your area</span>
              </li>
              <li>
                <span className="benefit-icon">✓</span>
                <span>No upfront costs or monthly fees</span>
              </li>
              <li>
                <span className="benefit-icon">✓</span>
                <span>Professional profile showcased to potential clients</span>
              </li>
              <li>
                <span className="benefit-icon">✓</span>
                <span>Simple, transparent referral process</span>
              </li>
              <li>
                <span className="benefit-icon">✓</span>
                <span>Dedicated support team to help you succeed</span>
              </li>
            </ul>
          </div>

          <form onSubmit={handleSubmit} className="agent-join-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="agentName" className="form-label">
                  Full Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="agentName"
                  name="agentName"
                  value={formData.agentName}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="agentEmail" className="form-label">
                  Email Address <span className="required">*</span>
                </label>
                <input
                  type="email"
                  id="agentEmail"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="phone" className="form-label">
                  Phone Number <span className="required">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="licenseNumber" className="form-label">
                  License Number <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="licenseNumber"
                  name="licenseNumber"
                  value={formData.licenseNumber}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="yearsExperience" className="form-label">
                  Years of Experience <span className="required">*</span>
                </label>
                <select
                  id="yearsExperience"
                  name="yearsExperience"
                  value={formData.yearsExperience}
                  onChange={handleInputChange}
                  className="form-select"
                  required
                >
                  <option value="">Select years</option>
                  <option value="0-2">0-2 years</option>
                  <option value="3-5">3-5 years</option>
                  <option value="6-10">6-10 years</option>
                  <option value="10+">10+ years</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="agencyName" className="form-label">
                  Agency Name
                </label>
                <input
                  type="text"
                  id="agencyName"
                  name="agencyName"
                  value={formData.agencyName}
                  onChange={handleInputChange}
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="serviceAreas" className="form-label">
                Service Areas <span className="required">*</span>
              </label>
              <input
                type="text"
                id="serviceAreas"
                name="serviceAreas"
                value={formData.serviceAreas}
                onChange={handleInputChange}
                className="form-input"
                placeholder="e.g., Sydney CBD, North Shore, Eastern Suburbs"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="website" className="form-label">
                Website / LinkedIn Profile
              </label>
              <input
                type="url"
                id="website"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                className="form-input"
                placeholder="https://..."
              />
            </div>

            <div className="form-group">
              <label htmlFor="agentMessage" className="form-label">
                Tell Us About Your Experience
              </label>
              <textarea
                id="agentMessage"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                className="form-textarea"
                rows="4"
                placeholder="Briefly describe your experience, specialties, and why you'd like to join our network..."
              ></textarea>
            </div>

            <button 
              type="submit" 
              className="form-submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting Application...' : 'Submit Application'}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default AgentJoinForm;
